/**
 * 최초 로그인 — 임시 비밀번호 강제 변경.
 * Figma「12 최초 로그인 — 비밀번호 강제 변경」(12679:5122).
 * mustChangePassword 시 로그인 후 이 화면으로 유도한다.
 */
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { PasswordInput } from '@/shared/components/ui/password-input';
import { Stack } from '@/shared/components/ui/row';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import {
  areForceChangePasswordRulesMet,
  evaluateForceChangePasswordRules,
} from '@/shared/lib/forceChangePasswordRules';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useAuthStore } from '@/stores/authStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Check, Circle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const schema = z
  .object({
    currentPassword: z.string().min(1, '현재(임시) 비밀번호를 입력해주세요'),
    newPassword: z.string().min(1, '새 비밀번호를 입력해주세요'),
    confirmPassword: z.string().min(1, '새 비밀번호 확인을 입력해주세요'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: '비밀번호가 일치하지 않습니다',
    path: ['confirmPassword'],
  })
  .refine((data) => data.newPassword !== data.currentPassword, {
    message: '임시 비밀번호와 다른 비밀번호를 입력해주세요',
    path: ['newPassword'],
  });

type FormValues = z.infer<typeof schema>;

type RuleRowProps = {
  met: boolean;
  label: string;
};

const RuleRow: React.FC<RuleRowProps> = ({ met, label }) => (
  <div
    className={cn(
      'flex items-center gap-2 text-xs',
      met ? 'text-foreground' : 'text-muted-foreground',
    )}
  >
    {met ? (
      <Check
        className="h-3.5 w-3.5 shrink-0 text-ruda-success"
        strokeWidth={3}
        aria-hidden
      />
    ) : (
      <Circle className="h-3.5 w-3.5 shrink-0" aria-hidden />
    )}
    <span>{label}</span>
  </div>
);

export const ForceChangePasswordPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const {
    changePassword,
    isAuthenticated,
    isInitialized,
    initialize,
    user,
  } = useAuthStore();

  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isInitialized) initialize();
  }, [isInitialized, initialize]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!isAuthenticated) {
      navigate({ to: ROUTE_PATHS.LOGIN } as never);
      return;
    }
    if (user && !user.mustChangePassword) {
      navigate({ to: ROUTE_PATHS.DASHBOARD } as never);
    }
  }, [isInitialized, isAuthenticated, user, navigate]);

  const currentPassword = form.watch('currentPassword');
  const newPassword = form.watch('newPassword');
  const confirmPassword = form.watch('confirmPassword');

  const rules = evaluateForceChangePasswordRules(
    newPassword,
    currentPassword || null,
  );

  const canSubmit =
    areForceChangePasswordRulesMet(rules) &&
    currentPassword.length > 0 &&
    confirmPassword.length > 0 &&
    newPassword === confirmPassword;

  const onSubmit = async (values: FormValues) => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await changePassword(values.currentPassword, values.newPassword);
      toast.success('비밀번호가 변경되었습니다. 서비스를 시작합니다.');
      navigate({ to: ROUTE_PATHS.DASHBOARD } as never);
    } catch (error) {
      handleApiError(error, toast);
    } finally {
      setSubmitting(false);
    }
  };

  const passwordTone =
    newPassword.length === 0
      ? 'default'
      : rules.minLength && rules.complexity
        ? 'success'
        : 'default';

  return (
    <div className="flex min-h-screen items-center justify-center bg-ruda-bg-dark p-6">
      <Card className="w-full max-w-[440px] rounded-16 border-0 shadow-[var(--shadow-elevated)]">
        <CardContent className="flex flex-col gap-4 px-9 pb-8 pt-9">
          <div className="flex items-center gap-2">
            <div
              className="size-6 shrink-0 rounded-[7px] bg-primary"
              aria-hidden
            />
            <p className="text-sm font-bold text-foreground">루다링크 Admin</p>
          </div>

          <h1 className="text-xl font-bold tracking-tight text-foreground">
            새 비밀번호를 설정해주세요
          </h1>

          <div className="space-y-0 text-[13px] leading-normal text-muted-foreground">
            <p>임시 비밀번호로 로그인했습니다.</p>
            <p>계속하려면 비밀번호를 변경해야 합니다.</p>
          </div>

          <Form {...form}>
            <Stack as="form" gap={16} onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-muted-foreground">
                      현재(임시) 비밀번호
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        size="sm"
                        autoComplete="current-password"
                        placeholder="임시 비밀번호"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-muted-foreground">
                      새 비밀번호
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        size="sm"
                        autoComplete="new-password"
                        tone={passwordTone}
                        placeholder="새 비밀번호"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-muted-foreground">
                      새 비밀번호 확인
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        size="sm"
                        autoComplete="new-password"
                        placeholder="새 비밀번호 재입력"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col gap-2 rounded-12 bg-muted/80 px-4 py-3.5">
                <RuleRow met={rules.minLength} label="10자 이상" />
                <RuleRow
                  met={rules.complexity}
                  label="영문 대/소문자·숫자·특수문자 중 3종 이상"
                />
                <RuleRow
                  met={rules.differentFromTemp}
                  label="임시 비밀번호와 다름"
                />
              </div>

              <Button
                type="submit"
                className="ruda-button w-full"
                disabled={!canSubmit}
                loading={submitting}
              >
                비밀번호 변경 후 시작하기
              </Button>
            </Stack>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
